<template>
  <!-- Only show section if user has viewed listings or has similar content -->
  <div v-if="shouldShowSection">
    <SlidingScrollableListingComponent
      title="You recently looked at"
      titleClasses="recent-title font-secondary text-xl font-bold"
      :listings="displayListings"
      :loading="isLoading"
      :error="error"
      :favorites="favorites"
      seeMoreText="View all recent listings"
      emptyMessage="You haven't viewed any listings yet"
      emptyActionText="Browse listings"
      :emptyActionLink="{ name: 'searchResults' }"
      :useTestData="false"
      sectionType="recent"
      @viewDetails="handleViewDetails"
      @toggleFavorite="handleToggleFavorite"
      @retry="handleRetry"
    />
    
    <!-- Debug info in development only -->
    <!-- <div v-if="isDevelopment && showDebugInfo" class="debug-info mt-2">
      <v-expansion-panels variant="accordion">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <v-chip size="small" color="info" class="mr-2">
              Debug: {{ recentlyViewedCount }} recent | {{ similarListingsCount }} similar
            </v-chip>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="debug-content">
              <h4>📊 Store State:</h4>
              <pre class="debug-json">{{ JSON.stringify(debugInfo, null, 2) }}</pre>
              
              <div class="debug-actions mt-3">
                <v-btn 
                  size="small" 
                  color="primary" 
                  @click="handleRefreshAll" 
                  class="mr-2"
                  :loading="isLoading"
                >
                  Refresh All Data
                </v-btn>
                <v-btn 
                  size="small" 
                  color="warning" 
                  @click="handleClearAll"
                  :loading="isLoading"
                >
                  Clear All Data
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div> -->
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch, ref } from 'vue';
import { useStore } from 'vuex';
import SlidingScrollableListingComponent from './SlidingScrollableListingComponent.vue';
import { useRouter } from 'vue-router';

// ===== PROPS & EMITS =====
const props = defineProps({
  favorites: { 
    type: Array, 
    default: () => [],
    validator: (value) => Array.isArray(value)
  },
  useTestData: { 
    type: Boolean, 
    default: false 
  },
  dataReady: { 
    type: Boolean, 
    default: false 
  },
  showDebugInfo: {
    type: Boolean,
    default: false
  }
});


const emit = defineEmits(['viewDetails', 'toggleFavorite']);

// ===== STORE SETUP =====
const store = useStore();
const router = useRouter(); // ✅ ADD: Router instance


// ===== COMPUTED PROPERTIES FROM STORE =====
const recentlyViewed = computed(() => store.getters['recentlyViewed/recentlyViewed']);
const similarListings = computed(() => store.getters['recentlyViewed/similarListings']);
// const displayListings = computed(() => store.getters['recentlyViewed/displayListings']);
const displayListings = computed(() => {
  const recentListings = store.getters['recentlyViewed/displayListings'] || [];
  
  // ✅ DEBUG: Log what we're getting
  if (process.env.NODE_ENV === 'development' && recentListings.length > 0) {
    console.log('🔍 RecentlyViewed displayListings sample:', {
      total: recentListings.length,
      sample: recentListings[0],
      hasCarDetails: !!recentListings[0]?.car_details,
      hasPropertyDetails: !!recentListings[0]?.property_details,
      category: recentListings[0]?.category?.name
    });
  }
  
  return recentListings;
});

// const shouldShowSection = computed(() => store.getters['recentlyViewed/shouldShowSection']);
const shouldShowSection = computed(() => {
  // Don't show until parent data is ready (prevents flash of empty state)
  if (!props.dataReady) return false;
  
  // Show if we have data or if using test data
  return store.getters['recentlyViewed/shouldShowSection'] || props.useTestData;
});
const isLoading = computed(() => store.getters['recentlyViewed/isLoading']);
const error = computed(() => store.getters['recentlyViewed/error']);
const viewStats = computed(() => store.getters['recentlyViewed/viewStats']);
const debugInfo = computed(() => store.getters['recentlyViewed/debugInfo']);

// ===== LOCAL COMPUTED PROPERTIES =====
const isDevelopment = computed(() => process.env.NODE_ENV === 'development');
const recentlyViewedCount = computed(() => store.getters['recentlyViewed/recentlyViewedCount']);
const similarListingsCount = computed(() => store.getters['recentlyViewed/similarListingsCount']);

// ===== DEVELOPMENT LOGGING =====
const devLog = (message, ...args) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(message, ...args);
  // }
};

const devError = (message, ...args) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.error(message, ...args);
  // } else {
  //   // Log critical errors in production for debugging
  //   console.error(message);
  // }
};

// ===== DEBOUNCED OPERATIONS =====
let refreshTimeout = null;
let similarFetchTimeout = null;

const debouncedRefreshSimilar = () => {
  clearTimeout(similarFetchTimeout);
  similarFetchTimeout = setTimeout(() => {
    store.dispatch('recentlyViewed/fetchSimilarListings').catch(error => {
      devError('❌ Failed to fetch similar listings:', error);
    });
  }, 1000);
};

// ===== UTILITY FUNCTIONS =====

/**
 * ✅ NEW: Generate URL-friendly slug from listing title
 */
const generateSlug = (title) => {
  if (!title) return 'listing';
  return title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

/**
 * ✅ NEW: Detect listing category type for proper routing
 */
const detectListingType = (listing) => {
  if (!listing) return 'general';
  
  // Check if it has property-specific data
  if (listing.property) return 'property';
  
  // Check category ID (assuming 3 and 4 are property categories)
  const categoryId = listing.category_id || listing.category?.id;
  if (categoryId === 3 || categoryId === 4) return 'property';
  
  // Check category name
  const categoryName = listing.category?.name?.toLowerCase() || '';
  if (categoryName.includes('property')) return 'property';
  
  return 'general';
};

/**
 * ✅ NEW: Get appropriate source parameter for routing
 */
const getSourceForListing = (listing) => {
  const listingType = detectListingType(listing);
  return listingType === 'property' ? 'recently-viewed-property' : 'recently-viewed-general';
};

// ===== EVENT HANDLERS =====




// ===== EVENT HANDLERS =====

/**
 * ✅ FIXED: Complete navigation handling with proper store management
 */
const handleViewDetails = async (listing) => {
  // devLog('🖱️ RecentlyViewed: handleViewDetails called', { 
  //   id: listing.id, 
  //   title: listing.title,
  //   categoryId: listing.category_id || listing.category?.id 
  // });
  
  try {
    // 1. Determine listing type and appropriate store
    const listingType = detectListingType(listing);
    const source = getSourceForListing(listing);
    
    // devLog('🔍 Listing analysis:', { 
    //   listingType, 
    //   source, 
    //   hasProperty: !!listing.property 
    // });
    
    // 2. Store the selected listing in appropriate store(s)
    if (listingType === 'property') {
      // Store in property filter store for property listings
      await store.dispatch('propertyFilter/setSelectedProperty', listing);
      devLog('✅ Stored in propertyFilter store');
    }
    
    // Always store in general FilterStore as fallback
    await store.dispatch('FilterStore/setSelectedListingItem', listing);
    // devLog('✅ Stored in FilterStore');
    
    // 3. Generate URL-friendly slug
    const slug = generateSlug(listing.title);
    
    // 4. Navigate to details page with proper parameters
    const routeConfig = {
      name: 'ListingDetails',
      params: {
        id: listing.id,
        slug: slug
      },
      query: { 
        source: source,
        category: listingType,
        from: 'recently-viewed'
      }
    };
    
    // devLog('🚀 Navigating to:', routeConfig);
    
    // ✅ FIXED: Now router is properly imported and available
    await router.push(routeConfig);
    // devLog('✅ Navigation completed');
    
    // 5. Mark as viewed in the backend (background operation)
    store.dispatch('recentlyViewed/markAsViewed', listing)
      .then(() => {
        // devLog('✅ [Background] Listing marked as viewed in backend');
      })
      .catch(error => {
        devError('⚠️ [Background] Failed to track listing view:', error);
        // Don't throw error since navigation already completed
      });
    
    // 6. Emit for parent component (if needed for additional handling)
    emit('viewDetails', listing);
    
  } catch (error) {
    // devError('❌ Failed to handle view details:', error);
    
    // Fallback: Still try to emit for basic navigation
    try {
      emit('viewDetails', listing);
    } catch (emitError) {
      // devError('❌ Even emit failed:', emitError);
    }
  }
};


/**
 * ✅ OPTIMIZED: Handle toggle favorite with validation
 */
const handleToggleFavorite = (listing) => {
  if (!listing || !listing.id) {
    devError('❌ Invalid listing for favorite toggle');
    return;
  }
  
  // devLog('💖 Toggle favorite:', listing.id);
  emit('toggleFavorite', listing);
};

/**
 * ✅ OPTIMIZED: Handle retry with debouncing
 */
const handleRetry = async () => {
  // devLog('🔄 Retrying recently viewed data fetch...');
  
  // Clear any pending refresh
  clearTimeout(refreshTimeout);
  
  try {
    await handleRefreshAll();
  } catch (error) {
    devError('❌ Retry failed:', error);
  }
};

/**
 * ✅ OPTIMIZED: Refresh all data with error handling
 */
const handleRefreshAll = async () => {
  // devLog('🔄 Refreshing all recently viewed data...');
  
  try {
    await store.dispatch('recentlyViewed/refreshAll');
    // devLog('✅ Data refreshed successfully');
  } catch (error) {
    // devError('❌ Failed to refresh data:', error);
    throw error; // Re-throw for caller handling
  }
};

/**
 * ✅ OPTIMIZED: Clear all with confirmation
 */
const handleClearAll = async () => {
  if (!confirm('Are you sure you want to clear all recently viewed data?')) {
    return;
  }
  
  try {
    await store.dispatch('recentlyViewed/clearAll');
    // devLog('🧹 All data cleared successfully');
  } catch (error) {
    devError('❌ Failed to clear data:', error);
  }
};

// ===== WATCHERS =====
/**
 * ✅ NEW: Watch for dataReady prop changes
 */
watch(() => props.dataReady, async (isReady, wasReady) => {
  if (isReady && !wasReady) {
    console.log('📊 Parent data is now ready, enhancing recently viewed...');
    
    const currentListings = store.getters['recentlyViewed/displayListings'];
    if (currentListings.length > 0) {
      const hasEnhancedData = currentListings.some(listing => 
        listing.car_details || listing.property_details
      );
      
      if (!hasEnhancedData) {
        await store.dispatch('recentlyViewed/getEnhancedRecentlyViewed');
      }
    }
  }
});

/**
 * ✅ OPTIMIZED: Simplified watcher with debouncing
 */
let filterStoreWatcher = null;

if (process.env.NODE_ENV === 'development') {
  filterStoreWatcher = watch(
    () => store.getters['recentlyViewed/recentlyViewedCount'],
    (newCount, oldCount) => {
      if (newCount > 0 && newCount !== oldCount) {
        debouncedRefreshSimilar();
      }
    }
  );
}

const debugRecentlyViewed = () => {
  console.group('👁️ RecentlyViewedListingsComponent Debug');
  console.log('shouldShowSection value:', shouldShowSection.value);
  
  // Debug store state
  console.log('Store state:', {
    recentlyViewed: store.state.recentlyViewed?.recentlyViewed?.length || 0,
    similarListings: store.state.recentlyViewed?.similarListings?.length || 0,
    displayListings: displayListings.value?.length || 0,
    dataReady: props.dataReady
  });
  
  // Debug the actual data
  if (displayListings.value?.length > 0) {
    console.log('Display listings sample:', displayListings.value[0]);
  } else {
    console.log('Display listings: empty array');
  }
  
  // Log the criteria used for shouldShowSection
  console.log('shouldShowSection calculation:', {
    storeValue: store.getters['recentlyViewed/shouldShowSection'],
    dataReady: props.dataReady,
    useTestData: props.useTestData,
    result: (store.getters['recentlyViewed/shouldShowSection'] || props.useTestData) && props.dataReady
  });
  
  console.groupEnd();
};

// Call this function on mount and when displayListings or dataReady changes
watch([displayListings, () => props.dataReady], () => {
  debugRecentlyViewed();
});



// ===== LIFECYCLE HOOKS =====
/**
 * ✅ CLEAN: Simple mount without store initialization
 */
onMounted(async () => {
  console.log('🚀 RecentlyViewedListingsComponent mounted');
  debugRecentlyViewed();
  devLog('🚀 RecentlyViewedListingsComponent mounted');
  
  // Only enhance if parent data is ready
  if (props.dataReady) {
    await enhanceExistingData();
  }
});

// ===== HELPER METHODS =====

/**
 * ✅ NEW: Extract enhancement logic into reusable method
 */
const enhanceExistingData = async () => {
  const currentListings = store.getters['recentlyViewed/displayListings'];
  if (currentListings.length > 0) {
    const hasEnhancedData = currentListings.some(listing => 
      listing.car_details || listing.property_details
    );
    
    if (!hasEnhancedData) {
      devLog('🎯 Enhancing existing recently viewed data...');
      await store.dispatch('recentlyViewed/getEnhancedRecentlyViewed');
    }
  }
};

/**
 * ✅ OPTIMIZED: Cleanup on unmount
 */
onUnmounted(() => {
  // devLog('🔌 RecentlyViewedListingsComponent unmounted');
  
  // Clean up timeouts
  clearTimeout(refreshTimeout);
  clearTimeout(similarFetchTimeout);
  
  // Clean up watchers
  if (filterStoreWatcher) {
    filterStoreWatcher();
  }
});

// ===== EXPOSE FOR PARENT COMPONENT =====
defineExpose({
  // Data access
  getRecentlyViewedCount: () => recentlyViewedCount.value,
  getSimilarListingsCount: () => similarListingsCount.value,
  getDisplayListingsCount: () => displayListings.value.length,
  
  // Actions
  refreshData: handleRefreshAll,
  clearAllData: handleClearAll,
  
  // State
  isLoading: () => isLoading.value,
  hasError: () => !!error.value,
  shouldShow: () => shouldShowSection.value,
  
  // Debug info (development only)
  getDebugInfo: () => process.env.NODE_ENV === 'development' ? debugInfo.value : null,
  getViewStats: () => viewStats.value
});
</script>

<style scoped>
.recent-title {
  color: #333;

}

/* ✅ OPTIMIZED: Cleaner debug styles */
.debug-info {
  text-align: left;
  margin-top: 8px;
  opacity: 0.9;
}

.debug-info .v-chip {
  font-size: 10px;
  opacity: 0.7;
}

.debug-content {
  max-width: 100%;
}

.debug-json {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 11px;
  line-height: 1.3;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid #e9ecef;
}

.debug-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .debug-json {
    font-size: 9px;
    max-height: 200px;
    padding: 8px;
  }
  
  .debug-actions {
    flex-direction: column;
  }
  
  .debug-actions .v-btn {
    width: 100%;
  }
}
</style>